<template>
  <div class="page history">
    <PageHeader title="Istoria" :subtitle="subtitle" icon="mdi-history">
      <DepartmentFilter
        v-model="department"
        :items="historyData"
        field="userDepartment"
        :departments="requestDepartments"
      />
    </PageHeader>

    <div class="page-body">
      <div class="history-grid">
        <section class="panel">
          <div class="panel-toolbar">
            <h2 class="panel-title">
              {{ department || "Toate demersurile" }}
              <span class="count">{{ filteredData.length }}</span>
            </h2>
            <span class="grow-spacer" />
            <SearchBar @search="handleSearch" />
            <ColumnSelect :headers="headers" :updateVisibleColumns="updateVisibleColumns" pref-key="history" />
          </div>
          <div v-if="isEngineer || isAdmin" class="legend">
            <span class="legend__swatch" aria-hidden="true"></span>Îndeplinite fără prețul deservirii
          </div>
          <v-data-table
            :mobile-breakpoint="0"
            :headers="visibleHeaders"
            :items="filteredData"
            :page.sync="page"
            :items-per-page.sync="itemsPerPage"
            :sort-by="sortBy"
            :sort-desc="sortDesc"
            :loading="isLoading"
            loading-text="Se încarcă istoria..."
            no-data-text="Nu există demersuri"
          >
            <template v-slot:item="{ item, index }">
              <tr
                class="history-row"
                :class="{
                  'light-green-row': index % 2 !== 0,
                  'row-rejected': item.status === 'Respins',
                  'row-no-price': missingPrice(item),
                  'is-selected': selectedItem && selectedItem.id === item.id,
                }"
                @click="onRowClick(item)"
              >
                <td
                  v-for="(header, hIndex) in visibleHeaders"
                  :key="hIndex"
                  :style="header.style ? header.style : {}"
                >
                  <StatusChip v-if="header.value === 'status'" :status="item.status" />
                  <v-btn
                    v-else-if="header.value === 'actions'"
                    outlined
                    color="primary"
                    @click.stop="onViewClick(item)"
                  >
                    Detalii
                  </v-btn>
                  <template v-else>
                    {{ getTableCellValue(item, header, index) }}
                  </template>
                </td>
              </tr>
            </template>
          </v-data-table>
        </section>
        <aside class="history-side">
          <RequestsTimeline v-if="selectedItem && selectedItemTimeline.length" :timelineData="selectedItemTimeline" />
          <div v-else class="panel side-empty">Alegeți un demers din tabel ca să vedeți prin ce etape a trecut.</div>
        </aside>
      </div>
    </div>

    <HistoryDetails
      ref="historyDetails"
      v-if="showHistoryDetails"
      :item="selectedItem"
      @updateRequestData="fetchHistoryData"
      @closeDialog="closeHistoryDetails"
    />
  </div>
</template>

<script>
import RequestsTimeline from "@/components/requests/RequestsTimeline";
import PageHeader from "@/components/layout/PageHeader.vue";
import StatusChip from "@/components/elements/StatusChip.vue";
import DepartmentFilter from "@/components/elements/DepartmentFilter.vue";
import ColumnSelect from "@/components/elements/ColumnSelect.vue";
import SearchBar from "@/components/elements/SearchBar.vue";
import HistoryDetails from "./HistoryDetails.vue";
import * as api from "./api";
import * as helper from "@/helper/helper.js";
import { mapGetters } from "vuex";
import { engineerRowsFor } from "@/config/periferice";

export default {
  name: "HistoryPage",
  components: {
    RequestsTimeline,
    PageHeader,
    StatusChip,
    DepartmentFilter,
    ColumnSelect,
    SearchBar,
    HistoryDetails,
  },
  data() {
    return {
      headers: [],
      historyData: [],
      department: "",
      approvalData: [],
      visibleHeaders: [],
      selectedItem: null,
      selectedItemTimeline: [],
      page: 1,
      itemsPerPage: 10,
      search: "",
      sortBy: "id",
      sortDesc: true,
      isLoading: true,
      showHistoryDetails: false,
      
    };
  },
  methods: {
    async fetchHistoryData() {
      // after "Îndeplinit" the list is reloaded: the panel shows the same request again, with its new step
      const keepId = this.selectedItem && this.selectedItem.id;
      try {
        // if (this.isLoading === false) return; // Prevent duplicate API calls

        if (this.isAdmin || this.isEngineer || this.isDtiSef || this.isRector || this.isContability) {
          const historyResponse = await api.getHistoryRequests();
          this.historyData = historyResponse;
        } else {
          const historyResponse = await api.getHistoryRequestsByUser(
            this.getUser.userId
          );
          this.historyData = historyResponse;
        }

        const wanted = Number(this.$route.query.cerere);
        const linked = wanted && this.historyData.find((request) => request.id === wanted);
        if (linked) {
          await this.onClick(linked);
          this.onViewClick(linked);
          this.$router.replace({ query: {} });
        } else {
          const again = keepId && this.filteredData.find((request) => request.id === keepId);
          await this.selectFirst(again);
        }

        this.isLoading = false;
      } catch (error) {
        console.error("Error fetching data:", error);
        this.isLoading = false;
      }
    },
    // the panel always shows a request of the list on screen (or nothing)
    async selectFirst(preferred) {
      const item = preferred || this.filteredData[0];
      if (item) {
        await this.onClick(item);
      } else {
        this.selectedItem = null;
        this.selectedItemTimeline = [];
      }
    },
    onRowClick(item) {
      this.onClick(item);
    },
    async onClick(item) {
      this.selectedItem = item;
      if (!item.timeline) {
        try {
          const timelineData = await api.getRequestTimelineData(item.id);
          item.timeline = timelineData;
          this.generateTimelineData(item);
        } catch (error) {
          console.error(
            `Error fetching timeline data for request ${item.id}:`,
            error
          );
        }
      } else {
        this.generateTimelineData(item);
      }
    },
    onViewClick(item) {
      // this.$router.push({ name: "historyDetails", params: { item: item } });
      this.selectedItem = item;
      this.showHistoryDetails = true;
      // this.$router.push({ 
      //   name: "historyDetails", 
      //   params: { 
      //     id: item.id, 
      //     item: item 
      //   }
      // });
    },
    generateTimelineData(item) {
      const timelineEvents =
        (item.timeline &&
          [...item.timeline].reverse().map((event) => ({
            number: event.approach_number,
            status: event.status,
            statusId: event.statusId,
            label: event.comments,
            date: event.date,
            user: event.user,
            comments: event.comments,
            files: event.files || [],
          }))) ||
        [];
      this.selectedItemTimeline = timelineEvents;
    },
    getTableCellValue(item, header, index) {
      if (header.value === "index") return (this.page - 1) * Math.max(this.itemsPerPage, 0) + index + 1;
      if (header.value === "user") return item.user;
      if (header.value === "approach_number")
        return item.approach_number != null ? item.approach_number : "";
      if (header.value === "status") return item.status;
      if (header.value === "equipment") return item.equipment;
      if (header.value === "model") return item.model;
      if (header.value === "registration_number")
        return item.registration_number;
      if (header.value === "created_at") return item.created_at;
      if (header.value === "cause") return item.cause;
      if (header.value === "userDepartment") return item.userDepartment;
      return "";
    },
    missingPrice(item) {
      return (this.isEngineer || this.isAdmin) && item.status === "Îndeplinit"
        && (item.service_price === null || parseFloat(item.service_price) === 0);
    },
    sortTable(header) {
      const result = helper.sortTable(header);
      return result;
    },
    updateVisibleColumns() {
      this.visibleHeaders = this.headers.filter((header) => header.visible);
    },
    handleSearch(searchTerm) {
      this.search = searchTerm;
    },
    closeHistoryDetails() {
    this.showHistoryDetails = false;
    },
  },
  watch: {
    // a department chip or a search that hides the selected request moves the panel to the first one shown
    filteredData(list) {
      if (this.isLoading) return;
      if (this.selectedItem && list.some((request) => request.id === this.selectedItem.id)) return;
      this.selectFirst();
    },
  },
  computed: {
    ...mapGetters(["getUser"]),
    isAdmin() {
      return this.$store.getters.getUser.role === "admin";
    },
    isContability() {
      return this.$store.getters.getUser.role === "contability";
    },
    isEngineer() {
      return this.$store.getters.getUser.role === "inginer" || this.$store.getters.getUser.role === "inginer-pc";
    },
    isEngineerNonPC() {
      return this.$store.getters.getUser.role === "inginer";
    },
    isEngineerPC() {
      return this.$store.getters.getUser.role === "inginer-pc";
    },
    isDtiSef() {
      return this.$store.getters.getUser.role === "dtisef";
    },
    isRector() {
      return this.$store.getters.getUser.role === "rector";
    },
    myDepartments() {
      return this.$store.getters.myDepartments;
    },
    // + the sub-units without a Șef whose requests a Șef approves
    requestDepartments() {
      return this.$store.getters.requestDepartments;
    },
    subtitle() {
      if (this.isAdmin || this.isEngineer || this.isDtiSef || this.isRector || this.isContability) {
        return "Toate demersurile, cu drumul fiecăruia prin aprobări.";
      }
      return this.myDepartments.length > 1
        ? "Demersurile tuturor departamentelor dvs. și cele care vă privesc, cu drumul fiecăruia prin aprobări."
        : "Demersurile departamentului dvs. și cele care vă privesc, cu drumul fiecăruia prin aprobări.";
    },
    filteredData() {
      let data = this.department
        ? this.historyData.filter((item) => item.userDepartment === this.department)
        : this.historyData;
      // each request keeps the engineer it was filed for
      if (this.isEngineerPC) {
        data = engineerRowsFor("inginer-pc", data);
      } else if (this.isEngineerNonPC) {
        data = engineerRowsFor("inginer", data);
      }
      return helper.filteredData(data, this.search);
    },
  },
  mounted() {
    this.headers = require("./historyData.json");
    this.visibleHeaders = this.headers.filter(
      (header) => header.defaultVisible
    );
    const many = this.requestDepartments.length > 1;
    this.headers.forEach((header) => {
      header.visible = header.defaultVisible || (many && header.value === "userDepartment");
    });
    this.visibleHeaders = this.headers.filter((header) => header.visible);
    
    // Fetch data once
    this.$nextTick(() => {
      this.fetchHistoryData();
    });
  },
};
</script>

<style scoped>
.history-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 20px;
  align-items: start;
}
.history-side {
  position: sticky;
  top: 16px;
}
.side-empty {
  padding: 28px 22px;
  color: var(--ink-2);
  line-height: 1.5;
}
.history-row {
  cursor: pointer;
}
.history-row.is-selected > td {
  background: #e3f3ea !important;
}
.history-row.is-selected > td:first-child {
  box-shadow: inset 4px 0 0 var(--green);
}
.row-no-price > td {
  background: var(--sky-bg) !important;
}
.legend {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  font-size: 0.9rem;
  color: var(--ink-2);
  border-bottom: 1px solid var(--line-soft);
}
.legend__swatch {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background: var(--sky-bg);
  border: 1px solid #b9d0ea;
}
@media (max-width: 1264px) {
  .history-grid {
    grid-template-columns: 1fr;
  }
  .history-side {
    position: static;
  }
}
</style>
